<template>
  <section
    class="rounded-lg border border-gray-200 bg-white p-4 md:p-5"
    data-cy="dry-run-card"
    :data-state="state"
  >
    <header
      class="flex items-center justify-between border-b border-gray-200 pb-3"
    >
      <h2 class="text-base font-semibold text-gray-900">Dry run</h2>
      <button
        v-if="canRun && runLabel"
        type="button"
        class="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline disabled:opacity-50"
        :disabled="busy"
        data-cy="run-dry-run"
        @click="emit('run')"
      >
        <i class="fas fa-play text-xs" aria-hidden="true" />
        {{ runLabel }}
      </button>
      <button
        v-else-if="canCancel && state === 'running'"
        type="button"
        class="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline disabled:opacity-50"
        :disabled="busy"
        data-cy="cancel-dry-run"
        @click="emit('cancel')"
      >
        <i class="fas fa-stop text-xs" aria-hidden="true" />
        Cancel dry run
      </button>
    </header>

    <div class="pt-3 text-sm">
      <p v-if="state === 'none'" class="text-gray-600" data-cy="dry-run-none">
        Not run yet. Nothing is saved during a dry run.
      </p>

      <Spinner
        v-else-if="state === 'running' && queued"
        message="Dry run queued"
        class="text-gray-900 [&_.spinner]:text-blue-600"
        role="status"
        data-cy="dry-run-queued"
      />

      <Spinner
        v-else-if="state === 'running'"
        :message="`Dry run running${problemsSoFarText(problemsSoFar)}`"
        class="text-gray-900 [&_.spinner]:text-blue-600"
        role="status"
        data-cy="dry-run-running"
      />

      <Spinner
        v-else-if="state === 'stopping'"
        message="Dry run stopping"
        class="text-gray-900 [&_.spinner]:text-blue-600"
        role="status"
        data-cy="dry-run-stopping"
      />

      <p
        v-else-if="state === 'stalled'"
        class="text-gray-900"
        data-cy="dry-run-stalled"
      >
        Dry run didn't finish.
      </p>

      <p
        v-else-if="state === 'cancelled'"
        class="text-gray-900"
        data-cy="dry-run-cancelled"
      >
        Dry run cancelled before it checked every row.
      </p>

      <template v-else-if="dryRun">
        <p v-if="!problems" class="text-gray-900" data-cy="dry-run-clean">
          <i class="fas fa-check mr-1 text-green-600" aria-hidden="true" />
          No problems found in {{ rowsText(rows ?? 0) }}
        </p>
        <ul v-else class="divide-y divide-gray-200" data-cy="dry-run-groups">
          <li
            v-for="group in dryRun.groups"
            :key="group.message"
            class="flex items-start gap-2 py-2 text-red-600 first:pt-0"
            data-cy="dry-run-group"
          >
            <i
              class="fas fa-exclamation-circle mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <span class="min-w-0 flex-1 break-words">{{ group.message }}</span>
            <span class="shrink-0 text-gray-900">{{
              rowsText(group.rows)
            }}</span>
          </li>
          <li
            v-if="!dryRun.groups.length"
            class="py-2 text-red-600 first:pt-0"
            data-cy="dry-run-problem-total"
          >
            <i class="fas fa-exclamation-circle mr-1" aria-hidden="true" />
            {{ rowsText(problems) }} with problems
          </li>
          <li
            v-if="dryRun.more_groups > 0"
            class="py-2 text-gray-600"
            data-cy="dry-run-more"
          >
            and {{ formatCount(dryRun.more_groups) }} more
          </li>
        </ul>

        <p
          v-if="warnings"
          class="mt-2 text-gray-900"
          data-cy="dry-run-warnings"
        >
          <i class="fas fa-info-circle mr-1 text-blue-600" aria-hidden="true" />
          {{ rowsText(warnings) }} with warnings
        </p>

        <p class="mt-2 text-gray-500" data-cy="dry-run-by">
          {{ runByText(dryRun) }}
        </p>
        <button
          v-if="canDownload"
          type="button"
          class="mt-2 inline-flex items-center gap-1 font-medium text-blue-600 hover:underline"
          data-cy="download-report"
          @click="emit('download')"
        >
          <i class="fas fa-download" aria-hidden="true" />
          Download report
        </button>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Spinner from "@/components/Spinner.vue";
import { formatCount } from "@/DataImports/importsHubRows";
import {
  type DryRun,
  problemsSoFarText,
  rowsText,
  runByText
} from "./reviewBatch";

const props = defineProps<{
  state: "none" | "running" | "stopping" | "finished" | "stalled" | "cancelled";
  dryRun: DryRun | null;
  rows: number | null;
  canRun: boolean;
  canDownload: boolean;
  canCancel?: boolean;
  busy: boolean;
  /** An optimistic run the server hasn't shown for a while; likely waiting in the queue. */
  queued?: boolean;
  /** Live count of the run in progress; null while an optimistic run has none yet. */
  problemsSoFar: number | null;
}>();

const emit = defineEmits<{ run: []; download: []; cancel: [] }>();

const problems = computed(() => props.dryRun?.problem_row_count ?? 0);
// Warned rows still import, so this line never changes the verdict above it.
const warnings = computed(() => props.dryRun?.warning_row_count ?? 0);
const runLabel = computed(() => {
  if (props.state === "none") return "Run dry run";
  return props.state === "running" || props.state === "stopping"
    ? ""
    : "Run again";
});
</script>
